import { Button, IconClose } from '@odekoteam/doppio';
import React, { useEffect, useRef, useState } from 'react';
import { Box, Flex, Paragraph, Image as ThemeUIImage } from 'theme-ui';

import FileUploadIcon from '@/assets/images/FileUpload.svg';
import { LoadingBox } from '@/components/Collection/LoadingBox';
import { PENDING_UPLOAD } from '@/components/Collection/utils';
import useImageUpload from '@/components/ItemIssues/useImageUpload';
import { useGetSignedUploadImageByKeyQuery } from '@/graphql/types/generated';

export default function ImageUpload({
  label,
  value,
  disabled,
  onChange,
  pendingFile,
  onFileSelect,
  externalError,
  requiredWidth,
  requiredHeight,
}: {
  label: string;
  value: string;
  disabled?: boolean;
  requiredWidth: number;
  requiredHeight: number;
  externalError?: string;
  pendingFile?: File | null;
  onChange: (key: string) => void;
  onFileSelect?: (file: File | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [internalError, setInternalError] = useState<string | null>(null);
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);

  const { upload, uploading, error: uploadError } = useImageUpload();

  const error = externalError || internalError || uploadError;

  const [{ data: previewData, fetching: previewFetching }] =
    useGetSignedUploadImageByKeyQuery({
      variables: { key: value },
      requestPolicy: 'network-only',
      pause: !value || !!pendingFile,
    });

  // Local preview for pending files
  useEffect(() => {
    if (!pendingFile) {
      setLocalPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(pendingFile);
    setLocalPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingFile]);

  const previewUrl = pendingFile
    ? localPreviewUrl ?? undefined
    : previewData?.signedUploadImageByKey?.url;
  const showPreview = pendingFile
    ? !!localPreviewUrl
    : value && !previewFetching && previewUrl;
  const isLoading = uploading || (!!value && !showPreview);

  const processFile = async (selectedFile: File) => {
    const validationError = await validateFile(
      selectedFile,
      requiredWidth,
      requiredHeight,
    );
    if (validationError) {
      setInternalError(validationError);
      return;
    }
    setInternalError(null);
    if (onFileSelect) {
      onFileSelect(selectedFile);
      onChange(PENDING_UPLOAD);
    } else {
      const result = await upload(selectedFile);
      if (result) {
        onChange(result.key);
      }
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const selectedFile = event.target.files?.[0];
    event.target.value = '';
    if (selectedFile) {
      await processFile(selectedFile);
    }
  };

  const handleDragEnter = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!uploading && !disabled) {
      setIsDragging(true);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    if (uploading || disabled) return;

    const droppedFile = event.dataTransfer.files[0];
    if (droppedFile) {
      await processFile(droppedFile);
    }
  };

  const handleBoxClick = () => {
    if (!uploading && !disabled) {
      inputRef.current?.click();
    }
  };

  const handleRemove = () => {
    if (onFileSelect) {
      onFileSelect(null);
    }
    onChange('');
  };

  return (
    <Flex sx={{ gap: 1, flexDirection: 'column' }}>
      <div>
        <span style={{ fontFamily: 'Roobert-Bold' }}>{`${label} `}</span>
        <span style={{ color: '#666' }}>
          {requiredWidth}px x {requiredHeight}px
        </span>
      </div>
      {showPreview ? (
        <Flex
          sx={{
            minHeight: 120,
            borderRadius: 2,
            overflow: 'hidden',
            border: '2px dashed',
            position: 'relative',
            alignItems: 'center',
            flexDirection: 'column',
            justifyContent: 'center',
            borderColor: 'secondaryHighlight',
          }}
        >
          <img
            alt={label}
            src={previewUrl}
            style={{ width: '100%', maxHeight: 200, objectFit: 'contain' }}
          />
          <Box
            sx={{
              top: 1,
              right: 1,
              position: 'absolute',
              display: disabled ? 'none' : 'block',
            }}
          >
            <Button
              variant="secondary"
              onPress={handleRemove}
              iconLeft={<IconClose />}
              style={{ borderRadius: 100 }}
            />
          </Box>
        </Flex>
      ) : isLoading ? (
        <Flex
          sx={{
            minHeight: 120,
            borderRadius: 2,
            border: '2px dashed',
            alignItems: 'center',
            flexDirection: 'column',
            justifyContent: 'center',
            borderColor: 'secondaryHighlight',
          }}
        >
          <LoadingBox />
        </Flex>
      ) : (
        <Flex
          onDrop={handleDrop}
          onClick={handleBoxClick}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          sx={{
            gap: 2,
            minHeight: 120,
            borderRadius: 2,
            cursor: 'pointer',
            border: '2px dashed',
            alignItems: 'center',
            flexDirection: 'column',
            justifyContent: 'center',
            borderColor: isDragging
              ? 'primary'
              : error
              ? 'errorDefault'
              : 'secondaryHighlight',
            backgroundColor: isDragging ? '#F9F9F7' : 'transparent',
            '&:hover': { borderColor: 'primary', backgroundColor: '#F9F9F7' },
          }}
        >
          <input
            type="file"
            accept=".png,image/png"
            ref={inputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <ThemeUIImage
            src={FileUploadIcon}
            sx={{ width: '18px', height: '18px' }}
          />
          <div style={{ textAlign: 'center' }}>
            <div>
              <span style={{ fontFamily: 'Roobert-Bold' }}>
                Click to upload
              </span>{' '}
              or drag
            </div>
            <div>and drop</div>
            <span style={{ fontSize: 10 }}>PNG (4MB max)</span>
          </div>
        </Flex>
      )}
      {error && <Paragraph sx={{ color: 'red' }}>{error}</Paragraph>}
    </Flex>
  );
}

function validateImageDimensions(
  file: File,
  requiredWidth: number,
  requiredHeight: number,
): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img.width === requiredWidth && img.height === requiredHeight);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(false);
    };
    img.src = objectUrl;
  });
}

async function validateFile(
  file: File,
  requiredWidth: number,
  requiredHeight: number,
): Promise<string | null> {
  if (file.type !== 'image/png') {
    return 'Invalid file type. Only PNG images are accepted.';
  }
  const hasValidDimensions = await validateImageDimensions(
    file,
    requiredWidth,
    requiredHeight,
  );
  if (!hasValidDimensions) {
    return `Invalid dimensions: image must be ${requiredWidth}px x ${requiredHeight}px.`;
  }
  return null;
}
